import React, { useState, useEffect, useRef, useCallback } from "react";
import style from "./DropDown.module.scss";
import CaretDown from "../../assets/svg/icons/common/CaretDown";

/**
 * DropDown component that displays a button with a dropdown menu
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to display in the dropdown button
 * @param {string} [props.bgColor] - Background color of the dropdown button
 * @param {string} [props.textColor="#000"] - Text color of the dropdown button
 * @param {Array<{text: string, onClick: Function, type?: string, hidden?: boolean}>} [props.options] - Array of options to display in the dropdown
 * @param {string} [props.size="medium"] - Size of the dropdown ("medium" or "small")
 * @param {string} [props.direction="down"] - Direction of the dropdown ("up" or "down")
 * @returns {JSX.Element} DropDown component
 */
function DropDown({
  children,
  bgColor,
  textColor = "#000",
  caretColor = "#3A383F",
  className = "",
  options,
  size = "medium",
  direction = "down",
  closeOnSelect = true,
  showArrow = false,
  smallFont = false,
  disabled = false,
}) {
  const [dropOpen, setDropOpen] = useState(false);
  const [shouldOpenUpward, setShouldOpenUpward] = useState(false);
  const dropDownRef = useRef(null);

  const calculateDropdownDirection = useCallback(() => {
    if (direction === "up") return true;

    if (!dropDownRef.current) return false;

    const rect = dropDownRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = options.length * 32; // dwell more on this later

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    return spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
  }, [direction]);

  /**
   * Effect to handle click outside the dropdown
   * Closes the dropdown when clicking outside its container
   */
  useEffect(() => {
    /**
     * Handles click events outside the dropdown
     * @param {MouseEvent} event - The click event
     */
    function handleClickOutside(event) {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setDropOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (dropOpen) {
        setShouldOpenUpward(calculateDropdownDirection());
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dropOpen, calculateDropdownDirection]);

  const handleDropdownToggle = () => {
    if (disabled) return;
    if (!dropOpen) {
      setShouldOpenUpward(calculateDropdownDirection());
    }
    setDropOpen(!dropOpen);
  };

  const handleOptionClick = (option) => {
    if (closeOnSelect) {
      setDropOpen(false);
    }
    option?.onClick();
  };

  const finalDirection =
    direction === "up" ? "up" : shouldOpenUpward ? "up" : "down";

  return (
    <div
      className={`${style.drop_down} ${className} ${dropOpen ? style.open : ""} ${finalDirection === "up" ? style.drop_up : ""} ${showArrow ? style.dropDownWithArrow : ""} ${smallFont ? style.smallFont : ""}`}
      ref={dropDownRef}
      data-dropdown
    >
      <button
        className={`${size == "medium" ? style.button : style.button_small} ${disabled ? style.disabled : ""}`}
        style={{
          backgroundColor: bgColor,
          color: textColor,
        }}
        onClick={handleDropdownToggle}
        disabled={disabled}
      >
        {children}

        {showArrow && (
          <div className={`${style.caret} ${dropOpen && style.open}`}>
            <CaretDown color={caretColor} />
          </div>
        )}
      </button>
      {dropOpen && (
        <span
          className={`${style.drop_content} ${size == "medium" ? "" : style.drop_content_small}`}
        >
          {options?.map((option) => (
            <React.Fragment key={option.text}>
              {!option?.hidden && (
                <p
                  key={option.text}
                  onClick={() => handleOptionClick(option)}
                  className={option.type == "danger" ? style.danger : ""}
                >
                  {option.text}
                </p>
              )}
            </React.Fragment>
          ))}
        </span>
      )}
    </div>
  );
}

export default DropDown;
